import { createContext, useContext, useEffect, useState, useMemo } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Per-host tenant brand resolver.
 *
 * On boot, hits GET /api/automaton/tenant-by-host?host=<window.location.host>.
 *  - If the response has tenant != null, the app skins itself with that tenant's
 *    logo / primary color / company name (so lab-x.toothmatrix.com feels like
 *    "Lab X" instead of "Toothmatrix").
 *  - Otherwise (root domain, preview env, localhost) we render the platform's
 *    master Toothmatrix/Toothrocket brand.
 *
 * Local development tip: append ?as=<slug> to the URL to *simulate* a tenant
 * subdomain when DNS isn't wired (e.g. /admin?as=acme).
 */
const TenantBrandContext = createContext({
    loading: true,
    tenant: null,        // { slug, status, branding: {...} } when on a subdomain
    subdomain: "",
    isPlatform: true,    // true when on toothmatrix.com root / preview / localhost
});

export function TenantBrandProvider({ children }) {
    const [state, setState] = useState({ loading: true, tenant: null, subdomain: "", isPlatform: true });

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const url = new URL(window.location.href);
                const overrideSlug = url.searchParams.get("as");
                const host = overrideSlug
                    ? `${overrideSlug}.toothmatrix.com`        // simulate for dev
                    : window.location.host;
                const { data } = await axios.get(`${API}/automaton/tenant-by-host`, { params: { host } });
                if (cancelled) return;
                setState({
                    loading: false,
                    tenant: data.tenant,
                    subdomain: data.subdomain || "",
                    isPlatform: !data.tenant,
                });
            } catch (e) {
                if (!cancelled) {
                    // Fail open — fall back to platform brand.
                    setState({ loading: false, tenant: null, subdomain: "", isPlatform: true });
                }
            }
        })();
        return () => { cancelled = true; };
    }, []);

    // Apply the tenant's primary color to the CSS variable layer.
    // This re-skins every component that uses `var(--accent)` or our cyan utility classes.
    useEffect(() => {
        if (state.tenant?.branding?.primary_color) {
            document.documentElement.style.setProperty("--tenant-primary", state.tenant.branding.primary_color);
            document.documentElement.dataset.tenantSlug = state.tenant.slug;
        } else {
            document.documentElement.style.removeProperty("--tenant-primary");
            delete document.documentElement.dataset.tenantSlug;
        }
    }, [state.tenant]);

    const value = useMemo(() => state, [state]);
    return <TenantBrandContext.Provider value={value}>{children}</TenantBrandContext.Provider>;
}

export function useTenantBrand() {
    return useContext(TenantBrandContext);
}
